/*
 * MigAwards search helpers: Spanish -> English query mapping shared by the
 * predictive search dropdown and the search results page (sections/main-search.liquid).
 * Only rewrites a query when at least one Spanish term is recognized.
 */
window.MigSearch = (function () {
  var PHRASES = [
    ['jugador mas valioso', 'mvp'],
    ['empleado del mes', 'employee of the month'],
    ['estudiante del mes', 'student of the month'],
    ['futbol americano', 'football'],
    ['artes marciales', 'martial arts'],
    ['primer lugar', '1st place'],
    ['primer puesto', '1st place'],
    ['segundo lugar', '2nd place'],
    ['segundo puesto', '2nd place'],
    ['tercer lugar', '3rd place'],
    ['tercer puesto', '3rd place'],
    ['cuarto lugar', '4th place'],
    ['quinto lugar', '5th place'],
    ['paquete de premios', 'award pack'],
    ['paquetes de premios', 'award pack']
  ];

  var WORDS = {
    trofeo: 'trophy', trofeos: 'trophy',
    medalla: 'medal', medallas: 'medal',
    placa: 'plaque', placas: 'plaque',
    copa: 'cup', copas: 'cup',
    premio: 'award', premios: 'award', galardon: 'award',
    reconocimiento: 'recognition',
    cinta: 'ribbon', cintas: 'ribbon', liston: 'ribbon', listones: 'ribbon', escarapela: 'rosette',
    cristal: 'crystal', vidrio: 'glass', acrilico: 'acrylic', madera: 'wood',
    estatuilla: 'figure', figura: 'figure',
    oro: 'gold', dorado: 'gold', plata: 'silver', plateado: 'silver', bronce: 'bronze',
    lugar: 'place', puesto: 'place', primer: '1st', primero: '1st', primera: '1st',
    segundo: '2nd', segunda: '2nd', tercer: '3rd', tercero: '3rd', tercera: '3rd',
    campeon: 'champion', campeona: 'champion', campeones: 'champion', campeonato: 'championship',
    participacion: 'participation', participante: 'participant',
    futbol: 'soccer', soccer: 'soccer',
    beisbol: 'baseball', pelota: 'baseball', softbol: 'softball',
    baloncesto: 'basketball', basquetbol: 'basketball', basquet: 'basketball', basket: 'basketball',
    voleibol: 'volleyball', volibol: 'volleyball', voley: 'volleyball',
    natacion: 'swimming', gimnasia: 'gymnastics',
    porrista: 'cheer', porristas: 'cheer', animadora: 'cheer', animadoras: 'cheer',
    baile: 'dance', danza: 'dance', ballet: 'dance',
    boliche: 'bowling', bolos: 'bowling',
    ajedrez: 'chess', lucha: 'wrestling', boxeo: 'boxing', tenis: 'tennis',
    atletismo: 'track', maraton: 'marathon', carrera: 'race', carreras: 'race',
    ciclismo: 'cycling', patinaje: 'skating', pesca: 'fishing',
    musica: 'music', ciencia: 'science', ciencias: 'science', deletreo: 'spelling bee',
    escuela: 'school', academico: 'academic', academica: 'academic',
    graduacion: 'graduation', graduado: 'graduation',
    maestro: 'teacher', maestra: 'teacher', profesor: 'teacher', profesora: 'teacher',
    entrenador: 'coach', entrenadora: 'coach',
    equipo: 'team', jugador: 'player', jugadora: 'player', portero: 'goalie',
    estrella: 'star', aguila: 'eagle',
    militar: 'military', bombero: 'firefighter', bomberos: 'firefighter', policia: 'police',
    empleado: 'employee', empleada: 'employee', voluntario: 'volunteer',
    carro: 'car', auto: 'car',
    personalizado: '', personalizada: '', personalizados: '', personalizadas: '',
    grabado: '', grabada: '', grabados: '', grabar: ''
  };

  var STOP = { de: 1, del: 1, la: 1, el: 1, los: 1, las: 1, para: 1, con: 1, y: 1, un: 1, una: 1, en: 1, por: 1, al: 1 };

  function normalize(text) {
    return String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
  }

  function translate(query) {
    var original = String(query || '').trim();
    var text = ' ' + normalize(original) + ' ';
    var hits = 0;

    PHRASES.forEach(function (pair) {
      var needle = ' ' + pair[0] + ' ';
      while (text.indexOf(needle) !== -1) {
        text = text.replace(needle, ' ' + pair[1] + ' ');
        hits++;
      }
    });

    var words = text.trim().split(' ').map(function (word) {
      if (Object.prototype.hasOwnProperty.call(WORDS, word)) {
        hits++;
        return WORDS[word];
      }
      return word;
    });

    if (!hits) return { query: original, changed: false };

    var result = words.filter(function (word) { return word && !STOP[word]; }).join(' ').trim();
    if (!result || result === normalize(original)) return { query: original, changed: false };
    return { query: result, changed: true };
  }

  return { translate: translate, normalize: normalize };
})();

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Send the English version of a Spanish query, keeping the original for the results page note
    const translated = window.MigSearch.translate(this.getQuery());
    if (!translated.changed) return;

    const form = event.target;
    let original = form.querySelector('input[name="mig_orig"]');
    if (!original) {
      original = document.createElement('input');
      original.type = 'hidden';
      original.name = 'mig_orig';
      form.appendChild(original);
    }
    original.value = this.getQuery();
    this.input.value = translated.query;
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    // Results are grouped (categories, products, pages), so step through every option rather than siblings
    const allElements = Array.from(this.querySelectorAll('li[role="option"]'));
    if (!allElements.length) return;

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    const index = allElements.indexOf(selectedElement);
    let activeElement = allElements[0];
    if (!moveUp && selectedElement) {
      activeElement = allElements[index + 1] || allElements[0];
    } else if (moveUp) {
      activeElement = allElements[index - 1] || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const translated = window.MigSearch.translate(searchTerm);
    const query = translated.query;
    const queryKey = query.replace(/ /g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const params = [
      `q=${encodeURIComponent(query)}`,
      `${encodeURIComponent('resources[type]')}=product,collection,page`,
      `${encodeURIComponent('resources[limit]')}=6`,
      `${encodeURIComponent('resources[limit_scope]')}=each`,
      `${encodeURIComponent('resources[options][unavailable_products]')}=last`,
      'section_id=predictive-search'
    ].join('&');

    fetch(`${routes.predictive_search_url}?${params}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        let resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        if (translated.changed) {
          const note = document.createElement('p');
          note.className = 'mig-ps-note';
          note.textContent = `Resultados para “${query}”`;
          resultsMarkup = note.outerHTML + resultsMarkup;
        }
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
